import React from "react";
import { fetchPullPlanTaktSectionContext } from "../../../../api/clientV2";
import type { ExistingWorkCode, PullPlanTaktSectionRef } from "../../../../models";
import { ActivityStatus } from "../../../../utils/p6";
import SectionRow from "./SectionRow/SectionRow";
import type { StagedZone } from "./UntaggedGroups/UntaggedGroups";
import SelectAllCheckbox from "./SectionRow/SelectAllCheckbox/SelectAllCheckbox";
import SelectionBar from "./SelectionBar/SelectionBar";
import { levelKey, zoneKey } from "../state";
import type {
  ActivityCommitted,
  ActivityMove,
  ActivityPatch,
  MoveTarget,
  TaktEdits,
} from "../state";
import {
  buildCommitted,
  buildSectionTree,
  effectiveSection,
  filterTree,
  isTagged,
  sectionMeta,
  treeActivities,
  treeMatches,
} from "../tree";
import type { SectionMeta, SectionTree, TreeActivity } from "../tree";
import "./StructureTab.css";

const EMPTY_SET: Set<string> = new Set();

/** `base` ∪ `add` (new set), or `base` unchanged when there's nothing to add. */
function withKeys(base: Set<string>, add: Set<string>): Set<string> {
  if (add.size === 0) return base;
  const next = new Set(base);
  add.forEach((k) => next.add(k));
  return next;
}

/** `set` minus every key starting with `prefix`, or `set` unchanged if none do. */
function dropByPrefix(set: Set<string>, prefix: string): Set<string> {
  const next = new Set(Array.from(set).filter((k) => !k.startsWith(prefix)));
  return next.size === set.size ? set : next;
}

/** Every zone + level key under one section's tree (one set — see collapsedNodes). */
function treeKeys(section: string, tree: SectionTree): Set<string> {
  const keys = new Set<string>();
  for (const z of tree.zones) {
    keys.add(zoneKey(section, z.takt_b));
    for (const l of z.levels) keys.add(levelKey(section, z.takt_b, l.takt_c));
  }
  return keys;
}

interface Props {
  scheduleId: number;
  sections: PullPlanTaktSectionRef[];
  edits: TaktEdits;
  /** Codes created earlier this session — passed to the work-code pickers. */
  sessionCodes?: ExistingWorkCode[];
  onRenameSection: (original: string, value: string) => void;
  onRenameZone: (section: string, taktB: string, value: string) => void;
  onRenameLevel: (section: string, taktB: string, taktC: string, value: string) => void;
  onApply: (activityId: number, patch: ActivityPatch, committed: ActivityCommitted) => void;
  onApplyMany: (moves: ActivityMove[]) => void;
  /** Delete a zone/level as one undo step: untag its activities (the `moves`)
   *  and clear any pending rename for it. The empty shell is dropped locally. */
  onDeleteZone: (section: string, taktB: string, moves: ActivityMove[]) => void;
  onDeleteLevel: (section: string, taktB: string, taktC: string, moves: ActivityMove[]) => void;
  onReorderSections: (order: string[]) => void;
  onReorderZones: (section: string, order: string[]) => void;
  onReorderLevels: (section: string, taktB: string, order: string[]) => void;
  /** Bumped when WBS-node mappings are saved (WBS tab) — forces a re-fetch of
   *  the loaded sections so the new mapped levels / node defaults show here. */
  reloadKey?: number;
}

const StructureTab: React.FC<Props> = ({
  scheduleId,
  sections,
  edits,
  sessionCodes,
  onRenameSection,
  onRenameZone,
  onRenameLevel,
  onApply,
  onApplyMany,
  onDeleteZone,
  onDeleteLevel,
  onReorderSections,
  onReorderZones,
  onReorderLevels,
  reloadKey,
}) => {
  const [expandedSections, setExpandedSections] = React.useState<Set<string>>(new Set());
  // Zones AND levels are open by default once a section expands; this one set
  // tracks the ones the user (or "Collapse all") has folded. Zone keys
  // (`section|b`) and level keys (`section|b|c`) never collide.
  const [collapsedNodes, setCollapsedNodes] = React.useState<Set<string>>(new Set());
  // Levels the user expanded past the compact ~5-row cap (show all rows).
  const [expandedLevels, setExpandedLevels] = React.useState<Set<string>>(new Set());
  const [actsById, setActsById] = React.useState<Record<number, TreeActivity>>({});
  const [colorByCode, setColorByCode] = React.useState<Record<string, string>>({});
  const [loadedSections, setLoadedSections] = React.useState<Set<string>>(new Set());
  const [loading, setLoading] = React.useState<Set<string>>(new Set());
  const [query, setQuery] = React.useState("");
  const dragName = React.useRef<string | null>(null);
  const dragSet = React.useRef<{ id: number; committed: ActivityCommitted }[] | null>(null);
  // Reordering a zone / level (Takt ordering) — what's being dragged.
  const dragZone = React.useRef<{ section: string; takt_b: string } | null>(null);
  const dragLevel = React.useRef<{ section: string; takt_b: string; takt_c: string } | null>(null);
  // True when the active drag carries the multi-selection (so the drop clears it).
  const dragFromSelection = React.useRef(false);
  // An activity (vs a zone/level/section) is being dragged — section headers
  // refuse it (a header drop has no zone, so the activity would land off-board).
  const [activityDragging, setActivityDragging] = React.useState(false);
  // Latest built trees, so drop handlers (defined before the memo) read current
  // zone/level order when reordering.
  const treesRef = React.useRef<Record<string, SectionTree>>({});
  const [selectedIds, setSelectedIds] = React.useState<Set<number>>(new Set());
  const [movedIds, setMovedIds] = React.useState<Set<number>>(new Set());
  const [scrollId, setScrollId] = React.useState<number | null>(null);
  // Focus one section so it fills the modal (the common "edit this section" job).
  const [focusedSection, setFocusedSection] = React.useState<string | null>(null);
  // After "Collapse all", sections opened/loaded afterward come up with their
  // zones/levels collapsed too (so re-expanding drills in, not cascades open).
  // Cleared by "Expand all".
  const collapseOnLoad = React.useRef(false);
  // User-added empty zones/levels with no activities yet, kept so they render and
  // accept drops; keyed by section / `${section}|${takt_b}`.
  const [extraZones, setExtraZones] = React.useState<Record<string, string[]>>({});
  const [extraLevels, setExtraLevels] = React.useState<Record<string, string[]>>({});
  // Per-section (zone, level) pairs mapped via WBS nodes — shown even when empty
  // so the Structure tab reflects the mapping, not just the activities present.
  const [mappedLevels, setMappedLevels] = React.useState<
    Record<string, Array<{ takt_b: string; takt_c: string }>>
  >({});

  const filtering = query.trim() !== "";
  const sectionOptions = React.useMemo(() => sections.map((s) => s.name), [sections]);

  React.useEffect(() => {
    if (movedIds.size === 0) return;
    const clear = () => {
      setMovedIds(new Set());
      setScrollId(null);
    };
    document.addEventListener("mousedown", clear);
    return () => document.removeEventListener("mousedown", clear);
  }, [movedIds]);

  const toggleSelect = (id: number) =>
    setSelectedIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const clearSelection = () => setSelectedIds(new Set());

  // Select-all for a leaf (level / untagged group): toggles exactly the shown
  // ids — all selected → clear them, otherwise add them all.
  const toggleSelectAll = (ids: number[]) => {
    if (!ids.length) return;
    setSelectedIds((prev) => {
      const allSel = ids.every((id) => prev.has(id));
      const next = new Set(prev);
      if (allSel) ids.forEach((id) => next.delete(id));
      else ids.forEach((id) => next.add(id));
      return next;
    });
  };

  const onActivityDragStart = (id: number, committed: ActivityCommitted) => {
    setActivityDragging(true);
    // A selected row drags the whole selection; an unselected row drags itself.
    dragFromSelection.current = selectedIds.has(id);
    const ids = selectedIds.has(id) ? Array.from(selectedIds) : [id];
    dragSet.current = ids
      .map((aid) => {
        if (aid === id) return { id, committed };
        const a = actsById[aid];
        return a ? { id: aid, committed: buildCommitted(a, edits.codeEdits, colorByCode) } : null;
      })
      .filter((d): d is { id: number; committed: ActivityCommitted } => d != null);
  };
  const onActivityDragEnd = () => {
    setActivityDragging(false);
    dragSet.current = null;
    dragFromSelection.current = false;
  };

  const reorderList = (list: string[], moved: string, before: string): string[] => {
    const out = list.filter((x) => x !== moved);
    const idx = out.indexOf(before);
    if (idx < 0) out.push(moved);
    else out.splice(idx, 0, moved);
    return out;
  };
  const onZoneDragStart = (section: string, taktB: string) => {
    dragZone.current = { section, takt_b: taktB };
  };
  const onLevelDragStart = (section: string, taktB: string, taktC: string) => {
    dragLevel.current = { section, takt_b: taktB, takt_c: taktC };
  };
  const onReorderDragEnd = () => {
    dragZone.current = null;
    dragLevel.current = null;
  };

  // Drop set → moves, skipping any activity already sitting at the target.
  const movesToTarget = (
    set: { id: number; committed: ActivityCommitted }[],
    target: MoveTarget,
  ): ActivityMove[] =>
    set
      .filter((d) => {
        const e = edits.activityEdits[d.id];
        const curSection = e?.section ?? d.committed.section;
        const curB = e?.takt_b ?? d.committed.takt_b;
        const curC = e?.takt_c ?? d.committed.takt_c;
        return !(
          target.section === curSection &&
          (target.takt_b || "") === (curB || "") &&
          (target.takt_c || "") === (curC || "")
        );
      })
      .map((d) => ({
        id: d.id,
        patch: {
          section: target.section,
          takt_a: target.takt_a,
          takt_b: target.takt_b,
          takt_c: target.takt_c,
        },
        committed: d.committed,
      }));

  const onDropMove = (target: MoveTarget) => {
    // Reorder a level within its zone (sets Takt ordering).
    const lvl = dragLevel.current;
    dragLevel.current = null;
    if (lvl) {
      if (
        target.section === lvl.section &&
        target.takt_b === lvl.takt_b &&
        target.takt_c &&
        target.takt_c !== lvl.takt_c
      ) {
        const zone = (treesRef.current[lvl.section]?.zones || []).find((z) => z.takt_b === lvl.takt_b);
        if (zone) {
          onReorderLevels(
            lvl.section,
            lvl.takt_b,
            reorderList(zone.levels.map((l) => l.takt_c), lvl.takt_c, target.takt_c),
          );
        }
      }
      return;
    }
    // Reorder a zone within its section.
    const zn = dragZone.current;
    dragZone.current = null;
    if (zn) {
      if (target.section === zn.section && target.takt_b && target.takt_b !== zn.takt_b) {
        const order = (treesRef.current[zn.section]?.zones || []).map((z) => z.takt_b);
        onReorderZones(zn.section, reorderList(order, zn.takt_b, target.takt_b));
      }
      return;
    }
    // Otherwise: move the dragged activity set into the target.
    const set = dragSet.current;
    dragSet.current = null;
    if (!set || !set.length) return;
    const moves = movesToTarget(set, target);
    if (!moves.length) return;
    onApplyMany(moves);
    revealMoved(moves.map((m) => m.id), target.section, target.takt_b, target.takt_c);
    if (dragFromSelection.current) clearSelection();
  };

  // After a drop, open the destination + flag the moved activities so they glow
  // (and the first scrolls into view) until the next click.
  const revealMoved = (ids: number[], section: string, taktB: string, taktC: string) => {
    if (!expandedSections.has(section)) void loadSection(section);
    setExpandedSections((prev) => (prev.has(section) ? prev : new Set(prev).add(section)));
    if (taktB) {
      setCollapsedNodes((prev) => {
        const zk = zoneKey(section, taktB);
        const lk = taktC ? levelKey(section, taktB, taktC) : null;
        if (!prev.has(zk) && !(lk && prev.has(lk))) return prev;
        const n = new Set(prev);
        n.delete(zk);
        if (lk) n.delete(lk);
        return n;
      });
    }
    setMovedIds(new Set(ids));
    setScrollId(ids[0] ?? null);
  };

  const ordered = React.useMemo(() => {
    if (edits.sectionOrder.length === 0) return sections;
    const byName = new Map(sections.map((s) => [s.name, s]));
    const out: PullPlanTaktSectionRef[] = [];
    for (const n of edits.sectionOrder) {
      const s = byName.get(n);
      if (s) out.push(s);
    }
    for (const s of sections) if (!edits.sectionOrder.includes(s.name)) out.push(s);
    return out;
  }, [sections, edits.sectionOrder]);

  const loadSection = React.useCallback(
    async (name: string, force = false) => {
      if (!force && (loadedSections.has(name) || loading.has(name))) return;
      setLoading((p) => new Set(p).add(name));
      try {
        const ctx = await fetchPullPlanTaktSectionContext(scheduleId, name);
        const acts: TreeActivity[] = ctx.activities.map((a) => ({
          id: a.id,
          task_name: a.task_name,
          status: a.status,
          takt_work: a.takt_work,
          takt_prefix: a.takt_prefix,
          section_name: a.section_name,
          takt_a: a.takt_a,
          takt_b: a.takt_b,
          takt_c: a.takt_c,
          wbs_path: a.wbs_path,
          start: a.restart_date,
          finish: a.reend_date,
          takt_sort_b: a.takt_sort_b,
          loaded_section: name,
          node_section: a.node_section,
          node_takt_a: a.node_takt_a,
          node_takt_b: a.node_takt_b,
          node_takt_c: a.node_takt_c,
        }));
        setActsById((p) => {
          const n = { ...p };
          for (const a of acts) n[a.id] = a;
          return n;
        });
        setMappedLevels((p) => ({ ...p, [name]: ctx.mapped_levels ?? [] }));
        setColorByCode((p) => ({ ...p, ...ctx.takt_work_colors }));
        setLoadedSections((p) => new Set(p).add(name));
        // A section opened while in the post-"Collapse all" state comes up with
        // its zones/levels folded, matching the rest of the collapsed board.
        if (collapseOnLoad.current) {
          const keys = treeKeys(name, buildSectionTree(acts));
          setCollapsedNodes((prev) => withKeys(prev, keys));
        }
      } finally {
        setLoading((p) => {
          const n = new Set(p);
          n.delete(name);
          return n;
        });
      }
    },
    [scheduleId, loadedSections, loading],
  );

  // A WBS-mapping save (reloadKey bump) changes the section structure + node
  // defaults — re-fetch every loaded section so the new mapped levels / reverted
  // overrides show. Staged Takt edits live in the parent `edits`, so they survive.
  const prevReloadKey = React.useRef(reloadKey);
  React.useEffect(() => {
    if (reloadKey === undefined || reloadKey === prevReloadKey.current) return;
    prevReloadKey.current = reloadKey;
    const names = Array.from(loadedSections);
    setLoadedSections(new Set());
    setActsById({});
    setMappedLevels({});
    names.forEach((name) => void loadSection(name, true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey]);

  // Searching / "untagged only" spans the whole board, so pull in every section.
  React.useEffect(() => {
    if (!filtering) return;
    ordered.forEach((s) => {
      if (!loadedSections.has(s.name)) void loadSection(s.name);
    });
  }, [filtering, ordered, loadedSections, loadSection]);

  // Build the visible tree per section. While filtering, build EVERY loaded
  // section (not just expanded ones) and prune to matches.
  const { trees, withContent } = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    const keep = (a: TreeActivity) => {
      if (!q) return true;
      const e = edits.activityEdits[a.id];
      const code = (e?.code ?? a.takt_work).toLowerCase();
      const label = (e?.name ?? a.takt_prefix).toLowerCase();
      return a.task_name.toLowerCase().includes(q) || code.includes(q) || label.includes(q);
    };
    const out: Record<string, SectionTree> = {};
    const has = new Set<string>();
    const all = Object.values(actsById);
    const names = focusedSection
      ? new Set([focusedSection])
      : filtering
        ? loadedSections
        : expandedSections;
    const prefixFor = (name: string) => `${name}|`;
    Array.from(names).forEach((name) => {
      if (!loadedSections.has(name)) return;
      const inSection = all.filter((a) => effectiveSection(a, edits.activityEdits) === name);
      const exLevels: Record<string, string[]> = {};
      const lOrder: Record<string, string[]> = {};
      const prefix = prefixFor(name);
      for (const [k, cs] of Object.entries(extraLevels)) {
        if (k.startsWith(prefix)) exLevels[k.slice(prefix.length)] = cs;
      }
      for (const [k, ord] of Object.entries(edits.levelOrder)) {
        if (k.startsWith(prefix)) lOrder[k.slice(prefix.length)] = ord;
      }
      // Every WBS-mapped (zone, level) shows even with no activities — the
      // structure follows the mapping, not just what's tagged. Merge them with
      // any zones/levels the user added by hand this session.
      const exZones = [...(extraZones[name] ?? [])];
      for (const m of mappedLevels[name] ?? []) {
        const b = (m.takt_b || "").trim();
        const c = (m.takt_c || "").trim();
        if (!b) continue;
        if (!exZones.includes(b)) exZones.push(b);
        if (c) {
          const arr = (exLevels[b] = exLevels[b] ?? []);
          if (!arr.includes(c)) arr.push(c);
        }
      }
      let t = buildSectionTree(
        inSection,
        edits.activityEdits,
        { zones: exZones, levels: exLevels },
        { zones: edits.zoneOrder[name] ?? [], levels: lOrder },
      );
      if (filtering) {
        // Show a section only if something matches, but then keep its FULL
        // structure (every level, incl. just-added ones) for tagging into.
        if (treeMatches(t, keep)) {
          has.add(name);
          t = filterTree(t, keep, keep);
        }
      }
      out[name] = t;
    });
    treesRef.current = out;
    return { trees: out, withContent: has };
  }, [
    actsById,
    expandedSections,
    loadedSections,
    edits.activityEdits,
    filtering,
    focusedSection,
    query,
    extraZones,
    extraLevels,
    mappedLevels,
    edits.zoneOrder,
    edits.levelOrder,
  ]);

  const focusSection = (n: string) => {
    setFocusedSection(n);
    void loadSection(n);
  };

  const toggleSection = (name: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else {
        next.add(name);
        void loadSection(name);
      }
      return next;
    });
  };

  // Every zone + level key under the given sections. Open sections are already
  // built in `trees`; for a loaded-but-collapsed section (absent from `trees`)
  // build a tree from its loaded activities so "Collapse all" folds it too.
  const descendantKeys = (names: string[]): Set<string> => {
    const keys = new Set<string>();
    for (const name of names) {
      const t =
        trees[name] ??
        buildSectionTree(
          Object.values(actsById).filter((a) => effectiveSection(a, edits.activityEdits) === name),
          edits.activityEdits,
        );
      treeKeys(name, t).forEach((k) => keys.add(k));
    }
    return keys;
  };

  // In full view the section itself is always open, so the toggle works on the
  // focused section's ZONES; otherwise it works on whole sections.
  const focusedZoneKeys =
    focusedSection && trees[focusedSection]
      ? trees[focusedSection].zones.map((z) => zoneKey(focusedSection, z.takt_b))
      : [];

  const allExpanded = focusedSection
    ? focusedZoneKeys.length > 0 && focusedZoneKeys.every((k) => !collapsedNodes.has(k))
    : ordered.length > 0 && ordered.every((s) => expandedSections.has(s.name));

  // Unfold every node (no prefix) or just one section's nodes (`${section}|`).
  const clearCollapsed = (prefix?: string) =>
    setCollapsedNodes((prev) => (prefix ? dropByPrefix(prev, prefix) : new Set<string>()));

  const expandAll = () => {
    if (focusedSection) {
      clearCollapsed(`${focusedSection}|`);
      return;
    }
    const names = ordered.map((s) => s.name);
    collapseOnLoad.current = false;
    setExpandedSections(new Set(names));
    clearCollapsed();
    setQuery("");
    names.forEach((n) => void loadSection(n));
  };

  // Collapse folds the whole subtree (sections + their zones + levels), so
  // re-opening a section drills in one level at a time instead of cascading
  // fully open. Sections opened later get the same treatment via collapseOnLoad.
  const collapseAll = () => {
    if (focusedSection) {
      setCollapsedNodes((prev) => withKeys(prev, descendantKeys([focusedSection])));
      return;
    }
    collapseOnLoad.current = true;
    setExpandedSections(new Set());
    setCollapsedNodes((prev) => withKeys(prev, descendantKeys(Array.from(loadedSections))));
    setQuery("");
  };

  // Fold/unfold one zone or level — its key format says which. During a search
  // every node is force-open, so a fold would only persist invisibly until the
  // search clears — ignore it.
  const toggleNode = (key: string) => {
    if (filtering) return;
    setCollapsedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleLevelExpand = (key: string) =>
    setExpandedLevels((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const addZone = (section: string, taktB: string) => {
    // `|` is the separator in zone/level keys; strip it so a typed pipe can't
    // forge a key that collides with another node.
    const b = taktB.replace(/\|/g, "").trim();
    if (!b) return;
    setExtraZones((prev) => {
      const cur = prev[section] ?? [];
      return cur.includes(b) ? prev : { ...prev, [section]: [...cur, b] };
    });
  };

  const addLevel = (section: string, taktB: string, taktC: string) => {
    const c = taktC.replace(/\|/g, "").trim();
    if (!c) return;
    const key = zoneKey(section, taktB);
    setExtraLevels((prev) => {
      const cur = prev[key] ?? [];
      return cur.includes(c) ? prev : { ...prev, [key]: [...cur, c] };
    });
  };

  // Takt A/B/C actually present in a section — scopes the form's placement
  // dropdowns; cascades when the user picks a different section.
  const getSectionMeta = React.useCallback(
    (section: string): SectionMeta => {
      const acts = Object.values(actsById).filter(
        (a) => effectiveSection(a, edits.activityEdits) === section,
      );
      const exLevels: Record<string, string[]> = {};
      const prefix = section + "|";
      for (const [k, cs] of Object.entries(extraLevels)) {
        if (k.startsWith(prefix)) exLevels[k.slice(prefix.length)] = cs;
      }
      return sectionMeta(acts, edits.activityEdits, {
        zones: extraZones[section] ?? [],
        levels: exLevels,
      });
    },
    [actsById, edits.activityEdits, extraZones, extraLevels],
  );

  // Picking another section in a dropdown lazy-loads it so its A/B/C populate.
  const onNeedSection = React.useCallback(
    (section: string) => {
      if (section && !loadedSections.has(section)) void loadSection(section);
    },
    [loadedSections, loadSection],
  );

  // Strip an activity's Takt entirely (code + zone) — used when DELETING a zone/
  // level, where its activities must leave the (vanishing) container.
  const fullUntagMoves = (ids: number[]): ActivityMove[] => {
    const moves: ActivityMove[] = [];
    ids.forEach((id) => {
      const a = actsById[id];
      if (a) {
        moves.push({
          id,
          patch: { code: "", name: "", color: "", takt_a: "", takt_b: "", takt_c: "" },
          committed: buildCommitted(a, edits.codeEdits, colorByCode),
        });
      }
    });
    return moves;
  };

  const removeFromBoard = (ids: number[]) => {
    // Drop the code AND clear the per-activity Section/A/B/C override. In the
    // modal the cleared override reverts to the node default (effectivePlacement
    // falls back to node_*), so the row snaps to its node's mapped level rather
    // than "No zone"; on save the override rows are actually deleted (full
    // untag → inherit). A mis-tag never persists.
    const moves = fullUntagMoves(ids);
    if (!moves.length) return;
    // Capture the zone/level each activity sat in so the structure survives.
    const zonesBySection: Record<string, Set<string>> = {};
    const levelsByZone: Record<string, Set<string>> = {};
    ids.forEach((id) => {
      const a = actsById[id];
      if (!a) return;
      const sec = effectiveSection(a, edits.activityEdits);
      const e = edits.activityEdits[id];
      const b = (e?.takt_b ?? a.takt_b) || "";
      const c = (e?.takt_c ?? a.takt_c) || "";
      if (!sec || !b) return;
      if (!zonesBySection[sec]) zonesBySection[sec] = new Set();
      zonesBySection[sec].add(b);
      if (c) {
        const k = zoneKey(sec, b);
        if (!levelsByZone[k]) levelsByZone[k] = new Set();
        levelsByZone[k].add(c);
      }
    });
    onApplyMany(moves);
    setExtraZones((p) => {
      const next = { ...p };
      for (const sec of Object.keys(zonesBySection)) {
        next[sec] = Array.from(new Set([...(next[sec] ?? []), ...Array.from(zonesBySection[sec])]));
      }
      return next;
    });
    setExtraLevels((p) => {
      const next = { ...p };
      for (const k of Object.keys(levelsByZone)) {
        next[k] = Array.from(new Set([...(next[k] ?? []), ...Array.from(levelsByZone[k])]));
      }
      return next;
    });
    setMovedIds(new Set(ids));
    setScrollId(null);
    // Deselect only the rows we removed — a single-row ✕ must not wipe a
    // multi-selection the user is still building elsewhere.
    setSelectedIds((prev) => {
      if (!prev.size) return prev;
      const next = new Set(prev);
      ids.forEach((id) => next.delete(id));
      return next.size === prev.size ? prev : next;
    });
  };

  const deselect = (ids: number[]) => {
    if (!ids.length) return;
    setSelectedIds((prev) => {
      if (!prev.size) return prev;
      const next = new Set(prev);
      ids.forEach((id) => next.delete(id));
      return next.size === prev.size ? prev : next;
    });
  };

  // Delete a zone outright: untag any activities it still holds (back to the
  // untagged rail) + clear pending renames as one undo step, then drop the empty
  // shell (and its levels) from local structure state. An empty shell with
  // nothing staged skips the buffer edit (no useless undo step).
  const deleteZone = (section: string, taktB: string) => {
    const zone = treesRef.current[section]?.zones.find((z) => z.takt_b === taktB);
    const ids: number[] = [];
    zone?.levels.forEach((l) => l.activities.forEach((a) => ids.push(a.id)));
    const zk = zoneKey(section, taktB);
    const hasRename =
      zk in edits.zoneNames ||
      Object.keys(edits.levelNames).some((k) => k.startsWith(zk + "|"));
    if (ids.length || hasRename) onDeleteZone(section, taktB, fullUntagMoves(ids));
    setExtraZones((p) => {
      const cur = p[section];
      if (!cur || !cur.includes(taktB)) return p;
      return { ...p, [section]: cur.filter((z) => z !== taktB) };
    });
    setExtraLevels((p) => {
      if (!(zk in p)) return p;
      const next = { ...p };
      delete next[zk];
      return next;
    });
    deselect(ids);
  };

  // Delete one level outright. Untag its activities + clear its rename (one undo
  // step), drop the level shell, and keep the parent zone alive (as an empty
  // shell) so deleting a zone's last level doesn't also drop the zone.
  const deleteLevel = (section: string, taktB: string, taktC: string) => {
    const level = treesRef.current[section]?.zones
      .find((z) => z.takt_b === taktB)
      ?.levels.find((l) => l.takt_c === taktC);
    const ids = level ? level.activities.map((a) => a.id) : [];
    const lk = levelKey(section, taktB, taktC);
    if (ids.length || lk in edits.levelNames)
      onDeleteLevel(section, taktB, taktC, fullUntagMoves(ids));
    setExtraZones((p) => {
      const cur = p[section] ?? [];
      return cur.includes(taktB) ? p : { ...p, [section]: [...cur, taktB] };
    });
    setExtraLevels((p) => {
      const zk = zoneKey(section, taktB);
      const cur = p[zk];
      if (!cur || !cur.includes(taktC)) return p;
      return { ...p, [zk]: cur.filter((c) => c !== taktC) };
    });
    deselect(ids);
  };

  // Assign one work code (+label/color) to every selected activity at once.
  const bulkAssignCode = (code: string, name: string, color: string) => {
    const moves: ActivityMove[] = [];
    Array.from(selectedIds).forEach((id) => {
      const a = actsById[id];
      if (a) {
        moves.push({
          id,
          patch: { code, name, color },
          committed: buildCommitted(a, edits.codeEdits, colorByCode),
        });
      }
    });
    if (!moves.length) return;
    onApplyMany(moves);
    setMovedIds(new Set(moves.map((m) => m.id)));
    setScrollId(null);
    clearSelection();
  };

  const onSectionHeadDrop = (targetName: string) => {
    // A section header has no zone, so it never accepts an activity (it would
    // land off-board). The header dragover already refuses activity drags; clear
    // any stray set defensively and fall through to section reordering only.
    if (dragSet.current) {
      dragSet.current = null;
      return;
    }
    const from = dragName.current;
    dragName.current = null;
    if (!from || from === targetName) return;
    const order = ordered.map((s) => s.name);
    const fromIdx = order.indexOf(from);
    const toIdx = order.indexOf(targetName);
    if (fromIdx < 0 || toIdx < 0) return;
    order.splice(toIdx, 0, order.splice(fromIdx, 1)[0]);
    onReorderSections(order);
  };

  // Memoized so its reference is stable — otherwise the `allVisibleIds` memo
  // below (which depends on it) would re-walk the whole board every render.
  const visibleSections = React.useMemo(
    () =>
      focusedSection
        ? ordered.filter((s) => s.name === focusedSection)
        : filtering
          ? ordered.filter((s) => withContent.has(s.name))
          : ordered,
    [focusedSection, filtering, ordered, withContent],
  );

  // Width (in chars) of the longest visible section name, so every row's
  // Full-view + counts start at the same x (aligned to the longest name, not
  // right-aligned). Drives --takt-section-name-len on the list.
  const sectionNameCh = React.useMemo(
    () => visibleSections.reduce((m, s) => Math.max(m, s.name.length), 3),
    [visibleSections],
  );

  // The draft's re-zoned/moved activities resolved to their EFFECTIVE placement,
  // so "Group untagged" votes on the user's in-progress fixes (new zones/levels,
  // corrected tags) instead of the stale committed tags. Activities the draft
  // untagged are excluded so their removed zone can't vote either.
  const { stagedZones, stagedExcluded } = React.useMemo(() => {
    const zones: StagedZone[] = [];
    const excluded: number[] = [];
    for (const k of Object.keys(edits.activityEdits)) {
      const id = Number(k);
      const a = actsById[id];
      if (!a) continue; // unloaded → can't resolve committed values
      const p = edits.activityEdits[id];
      const taktB = (p.takt_b ?? a.takt_b) || "";
      const taktC = (p.takt_c ?? a.takt_c) || "";
      if (!taktB || !taktC) {
        excluded.push(id);
        continue;
      }
      zones.push({
        activity_id: id,
        section_name: (p.section ?? a.section_name) || a.loaded_section || "",
        takt_a: (p.takt_a ?? a.takt_a) || "",
        takt_b: taktB,
        takt_c: taktC,
      });
    }
    return { stagedZones: zones, stagedExcluded: excluded };
  }, [edits.activityEdits, actsById]);

  // Every activity currently rendered (all sections, placed + untagged) — the
  // scope of the page-wide "Select all".
  const allVisibleIds = React.useMemo(() => {
    const ids: number[] = [];
    for (const s of visibleSections) {
      const t = trees[s.name];
      if (t) for (const a of treeActivities(t)) ids.push(a.id);
    }
    return ids;
  }, [visibleSections, trees]);

  // Per-section tagged vs untagged counts from the loaded activities — the LIVE
  // split that reflects staged edits, used once a section is loaded; before that
  // the backend `tagged_count`/`untagged_count` on the section ref are used.
  // Excludes completed work to match the backend counts (no count jump on load).
  const sectionSplit = React.useMemo(() => {
    const map: Record<string, { tagged: number; untagged: number }> = {};
    for (const a of Object.values(actsById)) {
      if (a.status === ActivityStatus.Complete) continue;
      const sec = effectiveSection(a, edits.activityEdits);
      const e = edits.activityEdits[a.id];
      const entry = map[sec] || (map[sec] = { tagged: 0, untagged: 0 });
      if (isTagged({ takt_work: e?.code ?? a.takt_work, takt_b: e?.takt_b ?? a.takt_b }))
        entry.tagged += 1;
      else entry.untagged += 1;
    }
    return map;
  }, [actsById, edits.activityEdits]);

  return (
    <div className="takt-editor-structure">
      <div className="takt-editor-structure-bar">
        <label className="takt-editor-structure-actions-all">
          <SelectAllCheckbox
            ids={allVisibleIds}
            selectedIds={selectedIds}
            onToggle={toggleSelectAll}
          />
          Select all
        </label>
        <span className="takt-editor-structure-divider" aria-hidden />
        <button
          type="button"
          className="takt-editor-structure-toggle"
          onClick={allExpanded ? collapseAll : expandAll}
        >
          {allExpanded ? "Collapse all" : "Expand all"}
        </button>
        <div className="takt-editor-structure-searchwrap">
          <input
            className="takt-editor-structure-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search activities or work codes (placed + untagged)…"
            aria-label="Search activities"
          />
          {query && (
            <button
              type="button"
              className="takt-editor-structure-search-clear"
              onClick={() => setQuery("")}
              aria-label="Clear search"
              title="Clear search"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      <div
        className="takt-editor-structure-list"
        style={{ ["--takt-section-name-len" as string]: sectionNameCh } as React.CSSProperties}
      >
        {filtering && loading.size > 0 && visibleSections.length === 0 && (
          <div className="takt-editor-hint">Searching…</div>
        )}
        {filtering && loading.size === 0 && visibleSections.length === 0 && (
          <div className="takt-editor-hint">No matching activities.</div>
        )}
        {visibleSections.map((section) => (
          <SectionRow
            key={section.name}
            scheduleId={scheduleId}
            section={section}
            split={
              sectionSplit[section.name] ?? {
                tagged: section.tagged_count,
                untagged: section.untagged_count,
              }
            }
            expanded={!!focusedSection || filtering || expandedSections.has(section.name)}
            loading={loading.has(section.name)}
            tree={trees[section.name] ?? null}
            colors={colorByCode}
            edits={edits}
            collapsedNodes={filtering ? EMPTY_SET : collapsedNodes}
            expandedLevels={expandedLevels}
            onToggleLevelExpand={toggleLevelExpand}
            movedIds={movedIds}
            scrollId={scrollId}
            selectedIds={selectedIds}
            selectionCount={selectedIds.size}
            onToggleSelect={toggleSelect}
            onToggleSelectAll={toggleSelectAll}
            getSectionMeta={getSectionMeta}
            onNeedSection={onNeedSection}
            sessionCodes={sessionCodes}
            onMoveToBoard={onApplyMany}
            stagedZones={stagedZones}
            excludedIds={stagedExcluded}
            isFocused={focusedSection === section.name}
            onFocusSection={focusSection}
            onExitFocus={() => {
              setFocusedSection(null);
              setQuery("");
            }}
            sectionOptions={sectionOptions}
            onToggleSection={toggleSection}
            onToggleNode={toggleNode}
            onAddZone={addZone}
            onAddLevel={addLevel}
            onDeleteZone={deleteZone}
            onDeleteLevel={deleteLevel}
            onUntagIds={removeFromBoard}
            onRemoveActivity={(id) => removeFromBoard([id])}
            onRenameSection={onRenameSection}
            onRenameZone={onRenameZone}
            onRenameLevel={onRenameLevel}
            onApply={onApply}
            onActivityDragStart={onActivityDragStart}
            onActivityDragEnd={onActivityDragEnd}
            onZoneDragStart={onZoneDragStart}
            onLevelDragStart={onLevelDragStart}
            onReorderDragEnd={onReorderDragEnd}
            onDropMove={onDropMove}
            activityDragging={activityDragging}
            onSectionDragStart={(n) => {
              dragName.current = n;
            }}
            onSectionHeadDrop={() => onSectionHeadDrop(section.name)}
          />
        ))}
      </div>

      <SelectionBar
        scheduleId={scheduleId}
        count={selectedIds.size}
        sessionCodes={sessionCodes}
        onAssign={bulkAssignCode}
        onRemove={() => removeFromBoard(Array.from(selectedIds))}
        onClear={clearSelection}
      />
    </div>
  );
};

export default StructureTab;
